/**
 * 可用模型概览组件
 *
 * 展示经过健康检查的可用模型列表，包括：
 * - 统计卡片（总数、平均延迟、最快、最慢）
 * - 筛选功能（供应商、延迟范围）
 * - 延迟分布条形图（可展开/折叠）
 * - 详细列表
 */

import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { llmAdminApi, type AvailableModelsWithLatencyResponse } from '../../services/llmAdminApi';

// 延迟等级配置
const LATENCY_LEVELS = [
  { max: 1000, label: '极快', icon: '⚡', color: '#22c55e' },   // green
  { max: 3000, label: '快', icon: '🚀', color: '#3b82f6' },     // blue
  { max: 5000, label: '正常', icon: '⏱️', color: '#eab308' },   // yellow
  { max: Infinity, label: '慢', icon: '🐌', color: '#ef4444' }, // red
];

const getLatencyLevel = (ms: number) => {
  return LATENCY_LEVELS.find(level => ms <= level.max) || LATENCY_LEVELS[LATENCY_LEVELS.length - 1];
};

const formatLatency = (ms: number): string => {
  if (ms >= 1000) {
    return `${(ms / 1000).toFixed(2)}s`;
  }
  return `${ms}ms`;
};

// 供应商颜色映射
const PROVIDER_COLORS: Record<string, string> = {
  iflow: '#8b5cf6',      // violet
  aliyuncs: '#06b6d4',   // cyan
  modelscope: '#f97316', // orange
  nvidia: '#10b981',     // green
  cucloud: '#ec4899',    // pink
};

const getProviderColor = (provider: string): string => {
  return PROVIDER_COLORS[provider] || '#6b7280'; // gray
};

interface AvailableModelsDashboardProps {
  // 无 props
}

export const AvailableModelsDashboard: React.FC<AvailableModelsDashboardProps> = () => {
  const [data, setData] = useState<AvailableModelsWithLatencyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);
  
  // 筛选状态
  const [selectedProvider, setSelectedProvider] = useState<string>('all');
  const [selectedLatencyRange, setSelectedLatencyRange] = useState<string>('all');

  // 获取数据
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await llmAdminApi.getAvailableModelsWithLatency();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : '获取数据失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 获取所有供应商列表
  const providers = data ? Array.from(new Set(data.models.map(m => m.provider))) : [];

  // 应用筛选
  const filteredModels = data?.models.filter(model => {
    // 供应商筛选
    if (selectedProvider !== 'all' && model.provider !== selectedProvider) {
      return false;
    }
    // 延迟范围筛选
    if (selectedLatencyRange !== 'all') {
      const ranges: Record<string, [number, number]> = {
        'fast': [0, 1000],
        'normal': [1000, 3000],
        'slow': [3000, 5000],
        'very-slow': [5000, Infinity],
      };
      const [min, max] = ranges[selectedLatencyRange] || [0, Infinity];
      if (model.response_time_ms < min || model.response_time_ms > max) {
        return false;
      }
    }
    return true;
  }) || [];

  // 显示前 15 条（折叠状态）
  const displayModels = expanded ? filteredModels : filteredModels.slice(0, 15);

  // 图表数据
  const chartData = displayModels.map(model => ({
    name: `${model.provider}/${model.model_use}`,
    latency: model.response_time_ms,
    provider: model.provider,
    model_use: model.model_use,
    ...getLatencyLevel(model.response_time_ms),
  }));

  // 统计卡片数据
  const stats = [
    { label: '模型总数', value: data?.total_count || 0, color: 'blue' },
    { label: '平均延迟', value: formatLatency(Math.round(data?.avg_latency_ms || 0)), color: 'green' },
    { label: '最快延迟', value: formatLatency(data?.min_latency_ms || 0), color: 'cyan' },
    { label: '最慢延迟', value: formatLatency(data?.max_latency_ms || 0), color: 'orange' },
  ];

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-center h-32">
          <div className="text-gray-500">加载中...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="text-red-600">{error}</div>
        <button
          onClick={fetchData}
          className="mt-2 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
        >
          重试
        </button>
      </div>
    );
  }

  if (!data || data.total_count === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="text-center text-gray-500">
          暂无可用模型数据
          <div className="mt-2 text-sm">
            请先执行健康检查以获取模型延迟数据
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 mb-6 overflow-hidden">
      {/* 标题栏 */}
      <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-blue-50 to-indigo-50">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">可用模型概览</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            基于最新健康检查结果，共 {data.total_count} 个模型
          </p>
        </div>
        <button
          onClick={fetchData}
          className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          刷新
        </button>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 border-b border-gray-200">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className={`p-3 rounded-lg bg-${stat.color}-50 border border-${stat.color}-200`}
          >
            <div className="text-xs text-gray-500">{stat.label}</div>
            <div className={`text-xl font-bold text-${stat.color}-700 mt-1`}>{stat.value}</div>
          </div>
        ))}
      </div>

      {/* 筛选器 */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 font-medium">供应商：</label>
            <select
              value={selectedProvider}
              onChange={(e) => setSelectedProvider(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">全部</option>
              {providers.map(provider => (
                <option key={provider} value={provider}>{provider}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600 font-medium">延迟范围：</label>
            <select
              value={selectedLatencyRange}
              onChange={(e) => setSelectedLatencyRange(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">全部</option>
              <option value="fast">&lt; 1s (极快)</option>
              <option value="normal">1-3s (快)</option>
              <option value="slow">3-5s (正常)</option>
              <option value="very-slow">&gt; 5s (慢)</option>
            </select>
          </div>
          {(selectedProvider !== 'all' || selectedLatencyRange !== 'all') && (
            <button
              onClick={() => {
                setSelectedProvider('all');
                setSelectedLatencyRange('all');
              }}
              className="px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800"
            >
              清除筛选
            </button>
          )}
          <div className="ml-auto text-sm text-gray-500">
            显示 {displayModels.length} / {filteredModels.length} 个模型
          </div>
        </div>
      </div>

      {/* 条形图 */}
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-sm font-medium text-gray-700 mb-3">延迟分布</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 5, right: 80, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" unit="ms" tick={{ fontSize: 11 }} stroke="#6b7280">
                <Tooltip />
              </XAxis>
              <YAxis
                type="category"
                dataKey="name"
                width={160}
                tick={{ fontSize: 10 }}
                stroke="#6b7280"
              />
              <Tooltip
                contentStyle={{
                  fontSize: 12,
                  borderRadius: 6,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
                // @ts-expect-error Recharts tooltip formatter type
                formatter={(value: number) => [formatLatency(value), '延迟']}
                labelFormatter={(label) => `模型：${label}`}
              />
              <Bar dataKey="latency" radius={[0, 4, 4, 0]} barSize={20}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 详细列表 */}
      <div className="overflow-hidden">
        <div className="p-4 bg-gray-50 border-b border-gray-200">
          <h3 className="text-sm font-medium text-gray-700">详细列表</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-center p-3 font-medium text-gray-600 w-16">排名</th>
                <th className="text-left p-3 font-medium text-gray-600">供应商</th>
                <th className="text-left p-3 font-medium text-gray-600">模型</th>
                <th className="text-right p-3 font-medium text-gray-600">延迟</th>
                <th className="text-center p-3 font-medium text-gray-600">等级</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {displayModels.map((model, index) => {
                const level = getLatencyLevel(model.response_time_ms);
                const rank = index + 1;
                const rankIcon = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : `${rank}`;
                
                return (
                  <tr key={`${model.provider}-${model.model_use}`} className="hover:bg-gray-50 transition-colors">
                    <td className="text-center p-3 text-lg">{rankIcon}</td>
                    <td className="p-3">
                      <span
                        className="px-2 py-1 rounded text-xs font-medium text-white"
                        style={{ backgroundColor: getProviderColor(model.provider) }}
                      >
                        {model.provider}
                      </span>
                    </td>
                    <td className="p-3 font-medium text-gray-700">
                      {model.model_use}
                      {model.model_name !== model.model_use && (
                        <span className="text-xs text-gray-400 ml-2">({model.model_name})</span>
                      )}
                    </td>
                    <td className="text-right p-3 font-mono text-gray-600">
                      {model.response_time_ms}ms
                    </td>
                    <td className="text-center p-3">
                      <span
                        className="px-2 py-1 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: `${level.color}20`,
                          color: level.color,
                        }}
                      >
                        {level.icon} {level.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* 展开/折叠按钮 */}
      {filteredModels.length > 15 && (
        <div className="p-4 border-t border-gray-200 bg-gray-50 text-center">
          <button
            onClick={() => setExpanded(!expanded)}
            className="px-6 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 mx-auto transition-all duration-150 hover:shadow-sm"
          >
            <span className="transition-transform duration-150" style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
              ▼
            </span>
            {expanded ? '折叠' : `展开剩余 ${filteredModels.length - 15} 个模型`}
          </button>
        </div>
      )}

      {/* 底部说明 */}
      <div className="p-3 bg-blue-50 border-t border-blue-100 text-xs text-blue-700">
        <span className="font-medium">💡 说明：</span>
        数据来源于健康检查生成的配置文件，延迟数据为最近一次健康检查的实测结果。
        延迟等级：&lt;1s 极快 ⚡ | 1-3s 快 🚀 | 3-5s 正常 ⏱️ | &gt;5s 慢 🐌
      </div>
    </div>
  );
};
